/**
 * Prefixbox Collection Banners
 * 
 * Injects promotional banners into Prefixbox product grids at specified positions.
 * Configuration comes from Shopify theme settings (window.pfbxCollectionBanners).
 */
(function() {
  'use strict';

  // Banner configuration from Shopify theme settings
  const config = window.pfbxCollectionBanners || [];
  
  // Track which banners have been injected to prevent duplicates
  const injectedBanners = new Set();
  
  // Flag to prevent multiple simultaneous injection runs
  let isInjecting = false;

  // Track the current page number
  let currentPage = 1;

  /**
   * Extract the collection handle from the current URL
   */
  function getCurrentCollection() {
    const match = window.location.pathname.match(/\/collections\/([^\/\?#]+)/);
    return match ? match[1].toLowerCase() : null;
  }

  /**
   * Get current page number from Prefixbox pagination
   * Prefixbox uses 'pf-page' parameter in the URL
   */
  function getCurrentPage() {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Prefixbox uses 'pf-page' parameter
    const pfPageParam = urlParams.get('pf-page');
    if (pfPageParam) {
      return parseInt(pfPageParam, 10) || 1;
    }

    // Fallback: check for regular 'page' parameter
    const pageParam = urlParams.get('page');
    if (pageParam) {
      return parseInt(pageParam, 10) || 1;
    }

    // No page parameter means page 1
    return 1;
  }

  /**
   * Create the banner DOM element
   */
  function createBannerElement(banner) {
    const div = document.createElement('div');
    div.className = 'prefixbox-product-container pfbx-collection-banner-container';
    div.setAttribute('data-banner-position', banner.position);
    div.setAttribute('data-banner-collection', banner.collection);
    div.setAttribute('data-banner-page', banner.page);
    div.setAttribute('data-banner-style', banner.style || 'card');
    
    // Build overlay HTML if overlay text exists
    let overlayHTML = '';
    if (banner.overlayText && banner.overlayText.trim()) {
      const positionClass = `pfbx-overlay-${banner.overlayPosition || 'center'}`;
      overlayHTML = `
        <div class="pfbx-banner-overlay ${positionClass}" style="font-family: ${banner.overlayFont || 'inherit'}; color: ${banner.overlayColor || '#ffffff'};">
          ${banner.overlayText}
        </div>
      `;
    }

    // Check banner style
    const isFullImage = banner.style === 'full-image';
    
    // Build content section (only for card style)
    let contentHTML = '';
    if (!isFullImage) {
      contentHTML = `
        <div class="pfbx-banner-content">
          <h3 class="pfbx-banner-title">${banner.title}</h3>
          <p class="pfbx-banner-subtitle">${banner.subtitle}</p>
          <span class="pfbx-banner-btn">${banner.buttonText}</span>
        </div>
      `;
    }

    // Add style class for full-image
    const styleClass = isFullImage ? 'pfbx-banner-full-image' : '';
    
    div.innerHTML = `
      <div class="pfbx-collection-banner ${styleClass}">
        <a href="${banner.linkUrl}" class="pfbx-banner-link">
          <div class="pfbx-banner-image">
            <img src="${banner.image}" alt="${banner.title || 'Promotional banner'}" loading="lazy" />
            ${overlayHTML}
          </div>
          ${contentHTML}
        </a>
      </div>
    `;
    
    return div;
  }

  /**
   * Generate a unique key for each banner to track injection status
   */
  function getBannerKey(banner) {
    return `${banner.collection}-${banner.position}-${banner.page}`;
  }

  /**
   * Remove all existing banners (used when page changes)
   */
  function removeExistingBanners(grid) {
    const existingBanners = grid.querySelectorAll('.pfbx-collection-banner-container');
    existingBanners.forEach(banner => banner.remove());
    injectedBanners.clear();
  }

  /**
   * Main function to inject banners into the product grid
   */
  function injectBanners() {
    if (isInjecting) return;
    isInjecting = true;

    const currentCollection = getCurrentCollection();
    if (!currentCollection) {
      isInjecting = false;
      return;
    }

    const grid = document.querySelector('.prefixbox-products-container');
    if (!grid) {
      isInjecting = false;
      return;
    }

    // Get current page
    const pageNum = getCurrentPage();

    // Check if page changed - if so, clear old banners
    if (pageNum !== currentPage) {
      removeExistingBanners(grid);
      currentPage = pageNum;
    }

    // Filter banners for current collection AND current page (or page 0 for "all pages")
    const bannersForCollection = config
      .filter(b => {
        const matchesCollection = b.collection && b.collection.toLowerCase() === currentCollection && b.image;
        const matchesPage = b.page === 0 || b.page === pageNum;
        return matchesCollection && matchesPage;
      })
      .sort((a, b) => a.position - b.position);

    if (bannersForCollection.length === 0) {
      isInjecting = false;
      return;
    }

    const products = grid.querySelectorAll('.prefixbox-product-container:not(.pfbx-collection-banner-container)');
    
    if (products.length === 0) {
      isInjecting = false;
      return;
    }

    bannersForCollection.forEach(banner => {
      const bannerKey = getBannerKey(banner);
      
      if (injectedBanners.has(bannerKey)) return;
      
      const existingBanner = grid.querySelector(
        `[data-banner-position="${banner.position}"][data-banner-collection="${banner.collection}"][data-banner-page="${banner.page}"]`
      );
      if (existingBanner) {
        injectedBanners.add(bannerKey);
        return;
      }

      const bannerEl = createBannerElement(banner);
      const insertIndex = banner.position - 1;

      const currentProducts = grid.querySelectorAll('.prefixbox-product-container:not(.pfbx-collection-banner-container)');

      if (insertIndex < currentProducts.length) {
        currentProducts[insertIndex].before(bannerEl);
      } else {
        grid.appendChild(bannerEl);
      }
      
      injectedBanners.add(bannerKey);
    });

    isInjecting = false;
  }

  /**
   * Debounce helper
   */
  function debounce(func, wait) {
    let timeout;
    return function() {
      clearTimeout(timeout);
      timeout = setTimeout(func, wait);
    };
  }

  const debouncedInject = debounce(injectBanners, 150);

  /**
   * Initialize the banner injection system
   */
  function init() {
    const grid = document.querySelector('.prefixbox-products-container');
    
    if (grid) {
      // Set initial page
      currentPage = getCurrentPage();
      
      // Initial injection
      injectBanners();
      
      // Watch for changes to the grid (Prefixbox re-renders on pagination, filtering, etc.)
      const observer = new MutationObserver((mutations) => {
        const hasNewProducts = mutations.some(mutation => 
          Array.from(mutation.addedNodes).some(node => 
            node.nodeType === 1 && 
            node.classList && 
            node.classList.contains('prefixbox-product-container') &&
            !node.classList.contains('pfbx-collection-banner-container')
          )
        );
        
        if (hasNewProducts) {
          const newPage = getCurrentPage();
          if (newPage !== currentPage) {
            removeExistingBanners(grid);
            currentPage = newPage;
          }
          debouncedInject();
        }
      });
      
      observer.observe(grid, { childList: true });

      // Also watch for URL changes (for pagination via URL)
      let lastUrl = location.href;
      new MutationObserver(() => {
        if (location.href !== lastUrl) {
          lastUrl = location.href;
          const newPage = getCurrentPage();
          if (newPage !== currentPage) {
            setTimeout(() => {
              const currentGrid = document.querySelector('.prefixbox-products-container');
              if (currentGrid) {
                removeExistingBanners(currentGrid);
              }
              currentPage = newPage;
              injectedBanners.clear();
              injectBanners();
            }, 200);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });

    } else {
      setTimeout(init, 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();